import { supabase } from "@/lib/supabase";
import {
  brand as mockBrand,
  navLinks as mockNav,
  footerLinks as mockFooter,
  socialLinks as mockSocial,
  legalLinks as mockLegal,
  heroStats as mockHeroStats,
} from "@/mocks/siteData";

export interface SiteBrand {
  name: string;
  fullName: string;
  tagline: string;
  region: string;
  email: string;
  phone: string;
  whatsapp: string;
}

export interface SiteNavLink {
  label: string;
  href: string;
}

export interface SiteFooterColumn {
  heading: string;
  links: SiteNavLink[];
}

export interface SiteSocialLink {
  icon: string;
  href: string;
  label: string;
}

export interface HeroContent {
  eyebrow: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  description: string;
  primaryCta: SiteNavLink;
  secondaryCta: SiteNavLink;
  stats: { value: string; label: string }[];
}

export interface SiteSettings {
  brand: SiteBrand;
  navLinks: SiteNavLink[];
  footerLinks: SiteFooterColumn[];
  socialLinks: SiteSocialLink[];
  legalLinks: SiteNavLink[];
  hero: HeroContent;
}

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  brand: { ...mockBrand },
  navLinks: mockNav,
  footerLinks: mockFooter,
  socialLinks: mockSocial,
  legalLinks: mockLegal,
  hero: {
    eyebrow: mockBrand.fullName,
    title: "From Social Life to",
    titleAccent: "Soil Life.",
    subtitle: "Experience the Farmer. Explore the Village. Connect with the Soil.",
    description:
      "Step away from the noise of city life and experience the beauty, simplicity, and stories of rural India. Rythugaru connects people with farms, farmers, villages, local food, nature, and traditional ways of life.",
    primaryCta: { label: "Explore Experiences", href: "#experiences" },
    secondaryCta: { label: "Become a Farm Partner", href: "/farm-partner" },
    stats: mockHeroStats,
  },
};

function asArray<T>(value: unknown, fallback: T[]): T[] {
  return Array.isArray(value) && value.length > 0 ? (value as T[]) : fallback;
}

function asRecord<T extends object>(value: unknown, fallback: T): T {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return { ...fallback, ...(value as Partial<T>) };
  }
  return fallback;
}

async function readSiteSettings(): Promise<SiteSettings> {
  const { data, error } = await supabase.from("site_settings").select("key, value");
  if (error) throw error;

  const rows = (data as { key: string; value: unknown }[] | null) || [];
  const map = new Map(rows.map((row) => [row.key, row.value]));

  const footerRow = asRecord(map.get("footer") as Record<string, unknown> | undefined, {} as {
    footerLinks?: SiteFooterColumn[];
    socialLinks?: SiteSocialLink[];
    legalLinks?: SiteNavLink[];
  });

  return {
    brand: asRecord<SiteBrand>(map.get("brand"), DEFAULT_SITE_SETTINGS.brand),
    navLinks: asArray<SiteNavLink>(map.get("nav"), DEFAULT_SITE_SETTINGS.navLinks),
    footerLinks: asArray<SiteFooterColumn>(footerRow.footerLinks, DEFAULT_SITE_SETTINGS.footerLinks),
    socialLinks: asArray<SiteSocialLink>(footerRow.socialLinks, DEFAULT_SITE_SETTINGS.socialLinks),
    legalLinks: asArray<SiteNavLink>(footerRow.legalLinks, DEFAULT_SITE_SETTINGS.legalLinks),
    hero: asRecord<HeroContent>(map.get("home_hero"), DEFAULT_SITE_SETTINGS.hero),
  };
}

/* --------------------------- lightweight shared store --------------------------- */

let cached: SiteSettings | null = null;
let pending: Promise<SiteSettings> | null = null;
const listeners = new Set<(settings: SiteSettings) => void>();

export function getSiteSettings(): SiteSettings {
  return cached || DEFAULT_SITE_SETTINGS;
}

export function subscribeSiteSettings(listener: (settings: SiteSettings) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function loadSiteSettings(): Promise<SiteSettings> {
  if (cached) return Promise.resolve(cached);
  if (pending) return pending;
  pending = readSiteSettings()
    .then((settings) => {
      cached = settings;
      listeners.forEach((listener) => listener(settings));
      return settings;
    })
    .catch(() => cached || DEFAULT_SITE_SETTINGS);
  return pending;
}

/** Used by the admin Homepage module after saving so the public site picks up changes. */
export function primeSiteSettings(settings: SiteSettings): void {
  cached = settings;
  listeners.forEach((listener) => listener(settings));
}